// Camp: Überblick über alle Räume (= Songs) des Camps, Briefs, wer da ist. Anmelden und Raum betreten.
import { useEffect, useState } from 'react';
import { Login } from './Login.tsx';
import { srsAbmelden } from '../srs/srs.ts';
import { useStudio } from '../state/store.ts';
import { api, type ProjectSummary } from '../api/client.ts';
import { joinRoom, leaveRoom } from '../collab/session.ts';
import { mediaStore } from '../collab/rtc.ts';
import { emptyProject } from '../state/demo.ts';
import { Panel, toast } from '../components/ui.tsx';
import { initials } from '../components/Topbar.tsx';

const store = { get: (k: string, d = '') => { try { return localStorage.getItem(k) ?? d; } catch { return d; } }, set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* privat */ } } };

export function Camp() {
  const user = useStudio((s) => s.user);
  const token = useStudio((s) => s.token);
  const online = useStudio((s) => s.online);
  const peers = useStudio((s) => s.peers);
  const project = useStudio((s) => s.project);
  const [camp, setCamp] = useState(store.get('srs.camp', 'Writing Camp'));
  const [rooms, setRooms] = useState<ProjectSummary[]>([]);

  const load = () => api.listProjects().then(setRooms).catch(() => setRooms([]));
  useEffect(() => { if (token) void load(); }, [token]);

  const enter = async (id: string) => {
    try {
      if (id !== project.id) {
        const p = await api.getProject(id);
        useStudio.getState().replaceProject(p, false);
      } else await api.saveProject(project);
      leaveRoom(); mediaStore.stop();
      joinRoom(id);
      useStudio.getState().setView('session');
      toast(`Raum betreten: ${rooms.find((r) => r.id === id)?.name ?? project.name}`);
    } catch (e) { toast((e as Error).message); }
  };
  const create = async () => {
    const name = prompt('Songtitel (Arbeitstitel)');
    if (!name) return;
    const p = emptyProject(name);
    try { useStudio.getState().replaceProject(p, false); await api.saveProject(p); await load(); await enter(p.id); }
    catch (e) { toast((e as Error).message); }
  };

  const signedIn = !!token;
  const writers = new Set(rooms.flatMap((r) => r.writers));
  const stats: [string | number, string][] = [[rooms.length || '–', 'Räume'], [writers.size || 1 + peers.length, 'Writer'], [rooms.filter((r) => r.srsWorkId).length, 'Werke in SRS'], [1 + peers.length, 'Online']];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: 10, height: '100%', minHeight: 0 }}>
      <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 10, minHeight: 0 }}>
        <div className="hero">
          <div>
            <div className="eyebrow">CAMP, SMART ROYALTY STUDIO</div>
            <input className="campname" value={camp} onChange={(e) => { setCamp(e.target.value); store.set('srs.camp', e.target.value); }} />
            <div className="sub">Hybrid: Studio und remote. Jeder Song ist ein Raum und wird als Werk in Smart Royalty Systems angelegt.</div>
          </div>
          <div className="stats">{stats.map(([a, b]) => <div key={b}><b>{a}</b><small>{b}</small></div>)}</div>
        </div>
        {!signedIn ? <Login /> : (
          <div style={{ overflow: 'auto', minHeight: 0 }} className="stack">
            <div className="rooms">
              {rooms.map((r, i) => {
                const here = r.id === project.id && online.collab;
                const stage = [r.tracks > 0, r.tracks >= 5, r.tracks >= 8, !!r.srsWorkId];
                return (
                  <section key={r.id} className={`panel room ${here ? 'here' : ''}`}>
                    <div className="row"><b className="h3" style={{ fontSize: 16 }}>Raum {i + 1}</b><span className="pill">{r.key}</span>{here ? <span className="pill green" style={{ marginLeft: 'auto' }}><i className="ldot" style={{ color: 'var(--green)' }} />live</span> : <span className="pill" style={{ marginLeft: 'auto' }}>{r.bpm} BPM</span>}</div>
                    <div className="t2" style={{ margin: '6px 0 8px', minHeight: 18 }}>{r.brief?.client || 'ohne Brief'}</div>
                    <div className="row" style={{ gap: 0, minHeight: 26 }}>{r.writers.slice(0, 5).map((w) => <span key={w} className="av" style={{ width: 26, height: 26, fontSize: 9, border: '2px solid #fff', background: w === user.id ? user.color : '#8a93a6' }}>{initials(w === user.id ? user.name : w.replace(/^u-/, ''))}</span>)}</div>
                    <div className="song"><b>{r.name}</b><small>{r.tracks} Spuren · {new Date(r.updatedAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</small></div>
                    <div className="stages">{['Idee', 'Topline', 'Produktion', 'Demo'].map((s, k) => <div key={s} className={stage[k] ? 'on' : ''}><i /><span>{s}</span></div>)}</div>
                    <div className="row" style={{ marginTop: 10 }}>
                      <span className={`pill ${r.srsWorkId ? 'green' : 'amber'}`}>{r.srsWorkId ?? 'Werk offen'}</span>
                      <button className={`btn small ${here ? '' : 'primary'}`} style={{ marginLeft: 'auto' }} onClick={() => here ? useStudio.getState().setView('session') : void enter(r.id)}>{here ? 'Zur Session' : 'Beitreten'}</button>
                    </div>
                  </section>
                );
              })}
              {!rooms.some((r) => r.id === project.id) && (
                <section className="panel room">
                  <div className="row"><b className="h3" style={{ fontSize: 16 }}>Aktuelles Projekt</b><span className="pill amber" style={{ marginLeft: 'auto' }}>nur lokal</span></div>
                  <div className="song"><b>{project.name}</b><small>{project.tracks.length} Spuren</small></div>
                  <div className="row" style={{ marginTop: 10 }}><button className="btn small primary" style={{ marginLeft: 'auto' }} onClick={() => void enter(project.id)}>Als Raum öffnen</button></div>
                </section>
              )}
              <button className="panel room newroom" onClick={() => void create()}><b>+ Neuer Raum</b><small>neuer Song, eigener Live-Raum</small></button>
            </div>
            <Panel title={<>Songs dieses Camps <span className="t2" style={{ fontFamily: 'var(--font)', fontWeight: 400, fontSize: 12.5, marginLeft: 8 }}>jeder Song wird als Werk in Smart Royalty Systems angelegt</span></>} right={<span className="pill">{rooms.length} Songs</span>}>
              <table className="amtable">
                <thead><tr><th>Titel</th><th>Raum</th><th>Brief</th><th>Tempo, Tonart</th><th>SRS</th></tr></thead>
                <tbody>{rooms.map((r, i) => <tr key={r.id}><td style={{ fontWeight: 600 }}>{r.name}</td><td className="t2">Raum {i + 1}</td><td className="t2">{r.brief?.client || '–'}</td><td className="mono" style={{ fontSize: 12 }}>{r.bpm} · {r.key}</td><td style={{ color: r.srsWorkId ? 'var(--blue)' : 'var(--t3)' }}>{r.srsWorkId ? 'Werk angelegt' : 'noch nicht'}</td></tr>)}</tbody>
              </table>
            </Panel>
          </div>
        )}
      </div>
      <Panel title="Im Camp" right={<><span className={`pill ${online.collab ? 'green' : ''}`}>{online.collab ? `live in „${project.name}“` : signedIn ? 'angemeldet' : 'nicht angemeldet'}</span>{signedIn && <button className="btn small" onClick={() => { leaveRoom(); mediaStore.stop(); if (useStudio.getState().srsSitzung) void srsAbmelden(); useStudio.getState().logout(); }}>Abmelden</button>}</>}>
        <div style={{ overflow: 'auto', flex: 1 }}>
          <div style={{ padding: '8px 16px' }}>
            {signedIn && [{ user, view: 'du' as string | undefined }, ...peers].map((p) => (
              <div key={p.user.id} className="row" style={{ padding: '7px 0', borderBottom: '1px solid var(--line-soft)' }}>
                <span className="av" style={{ margin: 0, border: 0, background: p.user.color }}>{initials(p.user.name)}</span>
                <div><b>{p.user.name}</b><div className="t2" style={{ fontSize: 12 }}>{[p.user.city, p.view].filter(Boolean).join(' · ')}</div></div>
              </div>
            ))}
          </div>
          <div className="sect">Briefs vom Label</div>
          <div className="stack" style={{ padding: '0 16px', gap: 8 }}>
            {rooms.filter((r) => r.brief?.client).map((r, i) => (
              <div key={r.id} className={`card ${r.id === project.id ? 'wash' : ''}`}><div className="row"><b className="h3">{r.brief!.client}</b><span className="pill" style={{ marginLeft: 'auto' }}>Raum {rooms.indexOf(r) + 1 || i + 1}</span></div><div className="t2" style={{ fontSize: 12.5, marginTop: 6 }}>{r.brief!.text}</div></div>
            ))}
            {!rooms.some((r) => r.brief?.client) && <div className="t2" style={{ fontSize: 12.5 }}>Briefs trägt ihr in Compose links oben ein.</div>}
          </div>
          <div className="sect">Zugang</div>
          <div style={{ padding: '0 16px 16px' }} className="t2">
            {['Anmeldung über das SRS-Konto (in Entwicklung: per Name)', 'Audio liegt verschlüsselt in der EU (Hetzner)', 'Eigene Plugins und Samples verlassen den Studio-Rechner nie'].map((t) => <div key={t} className="metric"><i className="ldot" style={{ color: 'var(--green)' }} />{t}</div>)}
          </div>
        </div>
      </Panel>
    </div>
  );
}

export { Login } from './Login.tsx';
